import { useDraggable, useDroppable } from '@dnd-kit/core';

import { cn } from '../../../shared/lib/cn';
import { FolderIcon, MoreIcon } from '../../../shared/ui/icons';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../shared/ui/menu';
import { countLine, type TFolder } from '../../../entities/folder';
import { dropId, type TDragged } from '../lib';

interface IFolderRowProps {
  folder: TFolder;
  noun: string;
  onOpen: (folder: TFolder) => void;
  onRename: (folder: TFolder) => void;
  onMove: (folder: TFolder) => void;
  onDelete: (folder: TFolder) => void;
}

// A folder row both takes drops (items, other folders) and can itself be dragged elsewhere.
export const FolderRow = ({
  folder,
  noun,
  onOpen,
  onRename,
  onMove,
  onDelete,
}: IFolderRowProps) => {
  const drop = useDroppable({ id: dropId(folder.id), data: { folderId: folder.id } });
  const dragged: TDragged = { kind: 'folder', folder };
  const drag = useDraggable({ id: `folder:${folder.id}`, data: dragged });

  return (
    <div
      ref={(node) => {
        drop.setNodeRef(node);
        drag.setNodeRef(node);
      }}
      {...drag.listeners}
      role="row"
      tabIndex={0}
      onClick={() => onOpen(folder)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onOpen(folder);
      }}
      className={cn(
        'group flex h-row-2line cursor-pointer items-center border-b border-border-row hover:bg-hover',
        drop.isOver && !drag.isDragging && 'bg-highlight',
        drag.isDragging && 'opacity-40',
      )}
    >
      <span role="cell" className="w-col-select shrink-0" />
      <span role="cell" className="flex min-w-0 flex-1 items-center gap-inset px-inset">
        <span className="flex shrink-0 text-muted">
          <FolderIcon />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-data font-medium text-primary">{folder.name}</span>
          <span className="truncate font-mono text-micro text-muted">
            {countLine(folder, noun)}
          </span>
        </span>
      </span>
      <span
        role="cell"
        className="flex w-col-row-menu shrink-0 justify-center"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <Menu>
          <MenuTrigger asChild>
            <button
              type="button"
              aria-label={`Folder ${folder.name}: more`}
              className="flex size-control-xs items-center justify-center rounded-item text-muted hover:bg-ghost hover:text-primary"
            >
              <MoreIcon />
            </button>
          </MenuTrigger>
          <MenuContent align="end">
            <MenuItem onSelect={() => onRename(folder)}>Rename</MenuItem>
            <MenuItem onSelect={() => onMove(folder)}>Move to…</MenuItem>
            <MenuSeparator />
            <MenuItem
              tone="danger"
              onSelect={() => onDelete(folder)}
              description={
                folder.itemCount + folder.folderCount === 0
                  ? 'It is empty'
                  : 'Only when empty — or with what it holds moved up a level'
              }
            >
              Delete
            </MenuItem>
          </MenuContent>
        </Menu>
      </span>
    </div>
  );
};
